import {
    getComputedValue,
    useCurationConfigValue
} from '../../../hooks/widgetConfiguration.js';

import useListeningSearchTimePeriodDuration from './useListeningSearchTimePeriodDuration.js';

const SEARCH_MAX_TIME_PERIOD_DURATION = 30;
const SEARCH_MAX_TIME_PERIOD_TYPE = RivalIQ.enums.timePeriod.type.LAST_THIRTY_DAYS;
const SAVED_SEARCH_TIME_PERIOD_DURATION = SEARCH_MAX_TIME_PERIOD_DURATION;

function getSavedSearchMaxTimePeriod(timezone) {
    // NOTE this is necessary because of #37402
    const timePeriod = RivalIQ.state.timePeriods.get(SEARCH_MAX_TIME_PERIOD_TYPE).toJSON();
    const maxTimePeriod = _.defaults({
        mainPeriodStart: moment.tz(timePeriod.mainPeriodStart, timezone).tz('UTC', true),
        mainPeriodEnd: moment.tz(timePeriod.mainPeriodEnd, timezone).tz('UTC', true),
        comparePeriodStart: moment.tz(timePeriod.comparePeriodStart, timezone).tz('UTC', true),
        comparePeriodEnd: moment.tz(timePeriod.comparePeriodEnd, timezone).tz('UTC', true)
    }, timePeriod);
    return maxTimePeriod;
}

function getEffectiveTimePeriod(period, duration) {
    if (period.id !== RivalIQ.enums.timePeriod.type.NOT_AVAILABLE) {
        return period;
    }

    let timePeriodType = RivalIQ.enums.timePeriod.type.NOT_AVAILABLE;
    if (duration === 7) {
        timePeriodType = RivalIQ.enums.timePeriod.type.LAST_SEVEN_DAYS;
    } else if (duration === 30) {
        timePeriodType = RivalIQ.enums.timePeriod.type.LAST_THIRTY_DAYS;
    }

    return RivalIQ.state.timePeriods.getDefinitionByType(timePeriodType);
}

// NOTE aligns a range to the start and end of date boundaries
function alignRange({end, start}) {
    return {
        end: moment.utc(end).endOf('day'),
        start: moment.utc(start).startOf('day')
    };
}

function containsRange(needle, haystack, {align = false} = {}) {
    const effectiveNeedle = (align ? alignRange(needle) : needle);
    const effectiveHaystack = (align ? alignRange(haystack) : haystack);

    // NOTE moment is exclusive at its boundaries, but we want to retain our
    // inclusive inputs (since we are dealing with time periods)
    const {endDate: end, startDate: start} = RivalIQ.utils.timePeriod.adjustBounds({
        bounds: {
            end: RivalIQ.enums.timePeriod.bounds.exclusive,
            start: RivalIQ.enums.timePeriod.bounds.exclusive
        },
        endDate: moment.utc(effectiveHaystack.end),
        startDate: moment.utc(effectiveHaystack.start)
    });

    return (moment.utc(effectiveNeedle.start).isBetween(start, end) && moment.utc(effectiveNeedle.end).isBetween(start, end));
}

function getPeriodMaskForTimePeriod(timePeriod, timezone, period = 'main') {
    const periodStartKey = `${period}PeriodStart`;
    const periodEndKey = `${period}PeriodEnd`;

    const range = {
        end: timePeriod[periodEndKey],
        start: timePeriod[periodStartKey]
    };

    const mask = _.reduce(RivalIQ.enums.timePeriod[`${period}PeriodDefinition`], (mask, value, key) => {
        // NOTE custom definitions do not have dates, so we early exit
        if (value.isCustom) {
            return mask | key;
        }

        const definition = RivalIQ.state.timePeriods.get(value.timePeriodId);
        // NOTE this is necessary because of #37402
        const periodRange = {
            end: moment.tz(definition.get(periodEndKey), timezone).tz('UTC', true),
            start: moment.tz(definition.get(periodStartKey), timezone).tz('UTC', true)
        };

        return (containsRange(periodRange, range) ? mask | key : mask);
    }, 0);

    return mask;
}

function isEmptyDateRangeResponse(dateRangeResponse) {
    return (
        !dateRangeResponse ||
        (dateRangeResponse?.isLoading === false && _.isEmpty(dateRangeResponse?.data))
    );
}

function hasDateRangeData(dateRangeResponse) {
    return (dateRangeResponse?.isLoading === false && !_.isEmpty(dateRangeResponse?.data));
}

export default function useListeningSearchTimePeriod(isSampleTopic, isInstantSearchForm, dateRangeResponse) {

    const plan = useCurationConfigValue('plan');
    const timezone = useCurationConfigValue('timezone');

    const curationTimePeriod = useCurationConfigValue('timePeriod');
    // NOTE we want to make sure we are dealing with UTC dates, but it is
    // possible that the curation time period retained its offset. by passing
    // `true` to `.tz` we tell moment only to futz with the offset, not the
    // time itself.
    const curationMainPeriodRange = {
        end: moment.utc(curationTimePeriod.mainPeriodEnd),
        start: moment.utc(curationTimePeriod.mainPeriodStart)
    };

    let timePeriodDuration = useListeningSearchTimePeriodDuration();
    if (isSampleTopic) {
        timePeriodDuration = SAVED_SEARCH_TIME_PERIOD_DURATION;
    }

    let timePeriod = null;
    let timePeriodDescription = null;
    let timePeriodMessage = null;
    let timePeriodOverride = null;
    let effectiveTimePeriod = null;
    let restrictionMessages = null;
    let restrictionPeriods = null;

    if (hasDateRangeData(dateRangeResponse)) {
        const {data: dateRanges} = dateRangeResponse;
        const {timezone: customDateRange} = dateRanges;

        // NOTE we want to make sure we are dealing with UTC dates, but the
        // `customDateRange` contains the offset by design. by passing `true` to
        // `.tz` we tell moment only to futz with the offset, not the time itself.
        timePeriod = effectiveTimePeriod = RivalIQ.utils.timePeriod.createTimePeriodFromStartAndEnd(
            moment.tz(customDateRange.start, timezone).tz('UTC', true),
            moment.tz(customDateRange.end, timezone).tz('UTC', true)
        );

        if (!containsRange(curationMainPeriodRange, dateRanges.utc)) {
            timePeriodOverride = RivalIQ.utils.timePeriod.convertTimePeriod(timePeriod);
        }

        restrictionMessages = {
            notAvailable: `This date is outside the configured topic date window.`,
            dateOutOfRangeError: `This date is outside the configured topic window.`
        };

        restrictionPeriods = {
            [RivalIQ.enums.timePeriod.periodType.MAIN_PERIOD]: {
                mask: getPeriodMaskForTimePeriod(timePeriod, timezone),
                min: timePeriod.mainPeriodStart,
                max: timePeriod.mainPeriodEnd
            },
            [RivalIQ.enums.timePeriod.periodType.COMPARE_PERIOD]: {
                disabled: true,
                mask: RivalIQ.enums.timePeriod.comparePeriodType.PREVIOUS_PERIOD,
                min: timePeriod.comparePeriodStart,
                max: timePeriod.comparePeriodEnd
            }
        };
    } else {
        const maxSearchTimePeriod = getSavedSearchMaxTimePeriod(timezone);

        timePeriod = RivalIQ.state.timePeriods.getDefinitionByType(RivalIQ.enums.timePeriod.type.NOT_AVAILABLE);
        if (!isInstantSearchForm) {
            if (plan?.has_full_instant_search) {
                timePeriodMessage = null;

                const maxMainPeriodRange = {
                    start: moment.utc(maxSearchTimePeriod.mainPeriodStart),
                    end: moment.utc(maxSearchTimePeriod.mainPeriodEnd)
                };

                // NOTE we have to align the ranges to date boundaries in this
                // case only because it's possible for our needle to be aligned
                // already but not our maximum. until we fix the upstream time
                // periods this is the simplest fix.
                if (!containsRange(curationMainPeriodRange, maxMainPeriodRange, {align: true})) { // eslint-disable-line object-curly-newline
                    // NOTE the current curation time period is incompatible
                    // with the current maximum time period for saved searches,
                    // so we must clamp it to that maximum
                    timePeriod = maxSearchTimePeriod;
                    if (isEmptyDateRangeResponse(dateRangeResponse)) {
                        timePeriodOverride = maxSearchTimePeriod;
                    }
                } else {
                    timePeriod = curationTimePeriod;
                }
            } else {
                timePeriodMessage = `Last ${timePeriodDuration} Days`;
                if (timePeriodDuration !== SEARCH_MAX_TIME_PERIOD_DURATION) {
                    timePeriodDescription = `Upgrade to view the last ${SEARCH_MAX_TIME_PERIOD_DURATION} days.`;
                } else {
                    timePeriodDescription = `Upgrade to customize the time period.`;
                }
            }
        }

        effectiveTimePeriod = getEffectiveTimePeriod(timePeriod, timePeriodDuration);

        restrictionPeriods = {
            [RivalIQ.enums.timePeriod.periodType.MAIN_PERIOD]: {
                mask: getPeriodMaskForTimePeriod(maxSearchTimePeriod, timezone),
                min: maxSearchTimePeriod.mainPeriodStart
            },
            [RivalIQ.enums.timePeriod.periodType.COMPARE_PERIOD]: {
                disabled: true,
                mask: (
                    RivalIQ.enums.timePeriod.comparePeriodType.PREVIOUS_PERIOD |
                    RivalIQ.enums.timePeriod.comparePeriodType.PREVIOUS_WEEK
                )
            }
        };
    }

    const dateRanges = getComputedValue('dateRanges', {
        timezone: timezone,
        timePeriod: effectiveTimePeriod
    });

    return {
        period: timePeriod,
        dateRanges: dateRanges,
        duration: timePeriodDuration,
        description: timePeriodDescription,
        effective: effectiveTimePeriod,
        // NOTE this is used for export headers
        label: RivalIQ.utils.timePeriod.getTimePeriodDisplayString(effectiveTimePeriod, effectiveTimePeriod.id, timezone),
        override: timePeriodOverride,
        message: timePeriodMessage,
        restrictions: {
            messages: _.extend({
                periodDisabled: 'Unavailable',
                notAvailable: `Social Listening data is only available for the last ${SEARCH_MAX_TIME_PERIOD_DURATION} days.`,
                dateOutOfRangeError: `Social listening data is only available for the last ${SEARCH_MAX_TIME_PERIOD_DURATION} days. Please select a more recent date.`
            }, restrictionMessages),
            periods: restrictionPeriods
        }
    };
}
